/* ============================================================================
   LUXURY POLICY — Editorial legal page behaviour
   · IntersectionObserver-driven reveal animations
   · Scroll-spy that highlights the active chapter in the TOC
   · Smooth-scroll with header offset for in-page anchor clicks
   ============================================================================ */

(function () {
  'use strict';

  if (typeof window === 'undefined') return;

  var sections = document.querySelectorAll('[data-lux-policy]');
  if (!sections.length) return;

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  sections.forEach(function (root) {
    initReveal(root);
    initToc(root);
    initSmoothScroll(root);
  });

  /* --------------------------------------------------------------------------
     Reveal — fade + lift on entry. Single observer per section.
     -------------------------------------------------------------------------- */
  function initReveal(root) {
    var targets = root.querySelectorAll('[data-lp-reveal]');
    if (!targets.length) return;

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, {
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.12
    });

    targets.forEach(function (el) { observer.observe(el); });
  }

  /* --------------------------------------------------------------------------
     TOC scroll-spy — keeps the currently in-view chapter highlighted.
     Uses IntersectionObserver with an offset for the fixed header.
     -------------------------------------------------------------------------- */
  function initToc(root) {
    var toc = root.querySelector('[data-lp-toc]');
    if (!toc) return;

    var links = toc.querySelectorAll('[data-lp-toc-link]');
    var chapters = root.querySelectorAll('[data-lp-chapter]');
    if (!links.length || !chapters.length) return;

    if (!('IntersectionObserver' in window)) return;

    var headerOffset = getHeaderHeight() + 32;
    var linkMap = {};
    links.forEach(function (link) {
      linkMap[link.getAttribute('data-target')] = link;
    });

    var visibleChapters = new Map();

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          visibleChapters.set(entry.target.id, entry.intersectionRatio);
        } else {
          visibleChapters.delete(entry.target.id);
        }
      });

      // Pick the topmost visible chapter (smallest top in viewport).
      var topId = null;
      var topY = Infinity;
      visibleChapters.forEach(function (_, id) {
        var el = document.getElementById(id);
        if (!el) return;
        var rect = el.getBoundingClientRect();
        if (rect.top < topY) {
          topY = rect.top;
          topId = id;
        }
      });

      // Fallback: if nothing is intersecting, find nearest chapter above viewport.
      if (!topId) {
        var nearest = null;
        var nearestDist = Infinity;
        chapters.forEach(function (ch) {
          var rect = ch.getBoundingClientRect();
          if (rect.top <= headerOffset + 1) {
            var dist = Math.abs(rect.top - headerOffset);
            if (dist < nearestDist) {
              nearestDist = dist;
              nearest = ch.id;
            }
          }
        });
        topId = nearest;
      }

      links.forEach(function (link) {
        link.classList.toggle('is-active', link.getAttribute('data-target') === topId);
      });
    }, {
      rootMargin: '-' + headerOffset + 'px 0px -55% 0px',
      threshold: [0, 0.25, 0.5, 0.75, 1]
    });

    chapters.forEach(function (ch) { observer.observe(ch); });
  }

  /* --------------------------------------------------------------------------
     Smooth scroll for in-page anchor links — accounts for the fixed header.
     -------------------------------------------------------------------------- */
  function initSmoothScroll(root) {
    var anchors = root.querySelectorAll('a[href^="#chapter-"]');
    anchors.forEach(function (a) {
      a.addEventListener('click', function (e) {
        var hash = a.getAttribute('href');
        if (!hash || hash.length < 2) return;
        var target = document.getElementById(hash.slice(1));
        if (!target) return;
        e.preventDefault();

        var offset = getHeaderHeight() + 24;
        var top = target.getBoundingClientRect().top + window.pageYOffset - offset;

        window.scrollTo({
          top: top,
          behavior: prefersReducedMotion ? 'auto' : 'smooth'
        });

        // Push the hash for accessibility / shareability without re-jumping.
        if (history && history.pushState) {
          history.pushState(null, '', hash);
        }
      });
    });
  }

  /* --------------------------------------------------------------------------
     Header height resolver — reads the CSS custom property the header sets.
     -------------------------------------------------------------------------- */
  function getHeaderHeight() {
    var styles = getComputedStyle(document.documentElement);
    var fromProp = parseInt(styles.getPropertyValue('--header-premium-height'), 10);
    if (!isNaN(fromProp) && fromProp > 0) return fromProp;
    var header = document.querySelector('.header-premium');
    return header ? header.offsetHeight : 84;
  }
})();
